// ✅ MIGRATED TO V2 DESIGN SYSTEM - Volvo/Teal Theme
// ✅ MIGRATED TO TANSTACK QUERY - Architecture 3 couches
/**
 * Composant de Configuration Connecteurs Comptables
 * Architecture 3 couches: Component → Hook (TanStack Query) → Service → HTTP
 */

import React, { useState, useEffect } from 'react';
import { Settings, CheckCircle, XCircle, RefreshCw, Link as LinkIcon, Info, Database, Cloud, Server } from 'lucide-react';
import { notifications } from '@/lib/notifications';
import { Button, Badge, Spinner } from '@/components/ui-v2';
import {
  useConnectorConfig,
  useConnectorLogs,
  useSaveConnectorConfig,
  useTestConnection,
  useSyncConnector,
} from '../hooks/useAccountingConnectors';

interface ConnectorConfig {
  connectorType: 'sage' | 'cegid' | 'quadra' | null;
  enabled: boolean;
  apiUrl: string;
  clientId: string;
  clientSecret: string;
  dossierId?: string;
  autoExport: boolean;
  lastSync: string | null;
  status: 'connected' | 'disconnected' | 'error';
}

const defaultConfig: ConnectorConfig = {
  connectorType: null,
  enabled: false,
  apiUrl: '',
  clientId: '',
  clientSecret: '',
  dossierId: '',
  autoExport: false,
  lastSync: null,
  status: 'disconnected'
};

const AccountingConnectorsSettings: React.FC = () => {
  // ✅ TanStack Query - Queries
  const { data: configData, isLoading: loadingConfig } = useConnectorConfig();
  const { data: logsData, isLoading: loadingLogs } = useConnectorLogs();

  // ✅ TanStack Query - Mutations
  const saveConfigMutation = useSaveConnectorConfig();
  const testConnectionMutation = useTestConnection();
  const syncMutation = useSyncConnector();

  // ✅ État local uniquement pour le formulaire éditable
  const [localConfig, setLocalConfig] = useState<ConnectorConfig>(defaultConfig);

  // Synchroniser le state local avec les données du serveur
  useEffect(() => {
    if (configData) {
      setLocalConfig(configData);
    }
  }, [configData]);

  const loading = loadingConfig;
  const logs = logsData || [];

  const handleSaveConfig = async () => {
    if (!localConfig.connectorType) {
      notifications.error('Veuillez sélectionner un type de connecteur');
      return;
    }
    try {
      await saveConfigMutation.mutateAsync({
        ...localConfig,
        connectorType: localConfig.connectorType,
      });
      notifications.saved('configuration');
    } catch (error) {
      notifications.saveError('configuration');
    }
  };

  const handleTestConnection = async () => {
    try {
      const result = await testConnectionMutation.mutateAsync();
      if (result.success) {
        notifications.success('Connexion réussie');
        setLocalConfig(prev => ({ ...prev, status: 'connected' }));
      } else {
        notifications.error('Échec de la connexion : ' + (result.error || 'Erreur inconnue'));
        setLocalConfig(prev => ({ ...prev, status: 'error' }));
      }
    } catch (error) {
      notifications.error('Erreur lors du test');
      setLocalConfig(prev => ({ ...prev, status: 'error' }));
    }
  };

  const handleManualSync = async () => {
    try {
      const result = await syncMutation.mutateAsync();
      if (result.success && result.data) {
        notifications.success(`Synchronisation réussie : ${result.data.exportedCount} factures exportées`);
      } else if (result.success) {
        notifications.success('Synchronisation réussie');
      } else {
        notifications.error('Échec de la synchronisation');
      }
    } catch (error) {
      notifications.error('Erreur lors de la synchronisation');
    }
  };

  // Icons SVG pour les connecteurs
  const getConnectorIcon = (type: string | null) => {
    const iconClass = "w-8 h-8";
    switch (type) {
      case 'sage':
        return <Cloud className={`${iconClass} text-green-600`} />;
      case 'cegid':
        return <Database className={`${iconClass} text-blue-600`} />;
      case 'quadra':
        return <Server className={`${iconClass} text-purple-600`} />;
      default:
        return <Settings className={`${iconClass} text-gray-400`} />;
    }
  };

  const getConnectorColor = (type: string | null) => {
    switch (type) {
      case 'sage': return 'bg-green-100';
      case 'cegid': return 'bg-blue-100';
      case 'quadra': return 'bg-purple-100';
      default: return 'bg-gray-100';
    }
  };

  const getStatusBadge = (status: string) => {
    const variants: Record<string, 'success' | 'pending' | 'alert'> = {
      connected: 'success',
      disconnected: 'pending',
      error: 'alert'
    };
    const labels: Record<string, string> = {
      connected: 'Connecté',
      disconnected: 'Déconnecté',
      error: 'Erreur'
    };
    return <Badge variant={variants[status]}>{labels[status]}</Badge>;
  };

  // Input classes V2
  const inputClasses = `w-full px-4 py-3 bg-white/40 border border-white/50 rounded-2xl
    focus:ring-2 focus:ring-teal-500 focus:border-teal-500 focus:bg-white
    hover:bg-white hover:border-teal-400 hover:shadow-lg hover:shadow-teal-100/50
    transition-all duration-300 outline-none placeholder:text-gray-400`;

  const checkboxClasses = `w-5 h-5 text-teal-600 border-gray-300 rounded-lg focus:ring-teal-500
    cursor-pointer`;

  // Card classes V2 - transparent par défaut, opaque au hover
  const cardClasses = `bg-white/20 backdrop-blur-xl rounded-3xl shadow-xl shadow-gray-300/10
    border border-white/30 p-6 hover:bg-white/70 hover:shadow-2xl hover:-translate-y-1
    transition-all duration-500`;

  if (loading) {
    return (
      <div className="px-8 py-12 flex items-center justify-center">
        <Spinner size="lg" />
      </div>
    );
  }

  return (
    <div className="px-4 md:px-8 pt-8 pb-12 space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <div className="p-3 bg-teal-100 rounded-2xl">
          <Settings className="w-8 h-8 text-teal-600" />
        </div>
        <div>
          <h1 className="text-xl md:text-2xl font-bold text-gray-900">Connecteurs Cabinets Comptables</h1>
          <p className="text-gray-500 mt-0.5 text-sm md:text-base">
            Configuration de l'export automatique vers Sage, Cegid ou Quadra
          </p>
        </div>
      </div>

      {/* Status Card */}
      <div className={cardClasses}>
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className={`p-4 rounded-2xl ${getConnectorColor(localConfig.connectorType)}`}>
              {getConnectorIcon(localConfig.connectorType)}
            </div>
            <div>
              <h3 className="text-lg sm:text-xl font-semibold text-gray-900">
                {localConfig.connectorType ? localConfig.connectorType.charAt(0).toUpperCase() + localConfig.connectorType.slice(1) : 'Aucun connecteur'}
              </h3>
              <p className="text-sm text-gray-500 mt-1">
                {localConfig.lastSync
                  ? `Dernière synchro : ${new Date(localConfig.lastSync).toLocaleString('fr-FR')}`
                  : 'Jamais synchronisé'}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {getStatusBadge(localConfig.status)}
            {localConfig.enabled && (
              <Button
                onClick={handleManualSync}
                disabled={syncMutation.isPending || localConfig.status !== 'connected'}
                variant="secondary"
                icon={<RefreshCw className={`w-4 h-4 ${syncMutation.isPending ? 'animate-spin' : ''}`} />}
              >
                {syncMutation.isPending ? 'Synchro...' : 'Synchroniser'}
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Configuration Card */}
      <div className={cardClasses}>
        <h3 className="text-lg font-semibold text-gray-900 mb-6 flex items-center gap-2">
          <Settings className="w-5 h-5 text-teal-600" />
          Configuration
        </h3>

        <div className="space-y-6">
          {/* Choix du connecteur */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-3">
              Type de connecteur
            </label>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              {(['sage', 'cegid', 'quadra'] as const).map((type) => (
                <button
                  key={type}
                  onClick={() => setLocalConfig({ ...localConfig, connectorType: type })}
                  className={`p-4 rounded-2xl border-2 transition-all duration-300 ${
                    localConfig.connectorType === type
                      ? 'border-teal-500 bg-teal-50/50 shadow-lg shadow-teal-100/50'
                      : 'border-white/50 bg-white/40 hover:border-teal-400 hover:bg-white hover:shadow-lg'
                  }`}
                >
                  <div className={`p-3 rounded-xl ${getConnectorColor(type)} mx-auto w-fit mb-2`}>
                    {getConnectorIcon(type)}
                  </div>
                  <p className="font-medium capitalize text-gray-900">{type}</p>
                </button>
              ))}
            </div>
          </div>

          {/* URL API */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              URL API
            </label>
            <input
              type="text"
              value={localConfig.apiUrl}
              onChange={(e) => setLocalConfig({ ...localConfig, apiUrl: e.target.value })}
              placeholder="https://api.sage.com"
              className={inputClasses}
            />
          </div>

          {/* Credentials */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Client ID
              </label>
              <input
                type="text"
                value={localConfig.clientId}
                onChange={(e) => setLocalConfig({ ...localConfig, clientId: e.target.value })}
                placeholder="your-client-id"
                className={inputClasses}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Client Secret
              </label>
              <input
                type="password"
                value={localConfig.clientSecret}
                onChange={(e) => setLocalConfig({ ...localConfig, clientSecret: e.target.value })}
                placeholder="••••••••"
                className={inputClasses}
              />
            </div>
          </div>

          {/* Dossier ID (pour Quadra/Cegid) */}
          {(localConfig.connectorType === 'quadra' || localConfig.connectorType === 'cegid') && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                ID Dossier
              </label>
              <input
                type="text"
                value={localConfig.dossierId}
                onChange={(e) => setLocalConfig({ ...localConfig, dossierId: e.target.value })}
                placeholder="DOSSIER123"
                className={inputClasses}
              />
            </div>
          )}

          {/* Options */}
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 sm:gap-8 pt-4 border-t border-white/30">
            <label className="flex items-center gap-3 cursor-pointer group">
              <input
                type="checkbox"
                checked={localConfig.enabled}
                onChange={(e) => setLocalConfig({ ...localConfig, enabled: e.target.checked })}
                className={checkboxClasses}
              />
              <span className="text-sm font-medium text-gray-700 group-hover:text-teal-600 transition-colors">
                Activer le connecteur
              </span>
            </label>

            <label className="flex items-center gap-3 cursor-pointer group">
              <input
                type="checkbox"
                checked={localConfig.autoExport}
                onChange={(e) => setLocalConfig({ ...localConfig, autoExport: e.target.checked })}
                className={checkboxClasses}
              />
              <span className="text-sm font-medium text-gray-700 group-hover:text-teal-600 transition-colors">
                Export automatique
              </span>
            </label>
          </div>

          {/* Actions */}
          <div className="flex flex-col sm:flex-row gap-3 pt-4">
            <Button
              onClick={handleSaveConfig}
              disabled={saveConfigMutation.isPending}
              variant="secondary"
              icon={saveConfigMutation.isPending ? <RefreshCw className="w-4 h-4 animate-spin" /> : <CheckCircle className="w-4 h-4" />}
            >
              {saveConfigMutation.isPending ? 'Sauvegarde...' : 'Sauvegarder'}
            </Button>

            <Button
              onClick={handleTestConnection}
              disabled={testConnectionMutation.isPending || !localConfig.connectorType}
              variant="ghost"
              icon={<LinkIcon className={`w-4 h-4 ${testConnectionMutation.isPending ? 'animate-pulse' : ''}`} />}
            >
              {testConnectionMutation.isPending ? 'Test...' : 'Tester la connexion'}
            </Button>
          </div>
        </div>
      </div>

      {/* Logs Card */}
      <div className={`${cardClasses} overflow-hidden p-0`}>
        <div className="px-6 py-4 border-b border-white/30">
          <h3 className="text-lg font-semibold text-gray-900">Logs de Synchronisation</h3>
        </div>

        <div className="divide-y divide-white/20 max-h-96 overflow-y-auto">
          {loadingLogs ? (
            <div className="px-6 py-12 flex justify-center">
              <Spinner size="md" />
            </div>
          ) : logs.length === 0 ? (
            <div className="px-6 py-12 text-center text-gray-500">
              <RefreshCw className="w-12 h-12 mx-auto mb-3 text-gray-300" />
              <p>Aucun log disponible</p>
            </div>
          ) : (
            logs.map((log: { date: string; message: string; status: string }, index: number) => (
              <div key={index} className="px-6 py-4 hover:bg-white/30 transition-colors">
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-3">
                    {log.status === 'success' ? (
                      <div className="p-1.5 bg-teal-100 rounded-lg">
                        <CheckCircle className="w-4 h-4 text-teal-600" />
                      </div>
                    ) : (
                      <div className="p-1.5 bg-red-100 rounded-lg">
                        <XCircle className="w-4 h-4 text-red-600" />
                      </div>
                    )}
                    <div>
                      <p className="text-sm text-gray-900">{log.message}</p>
                      <p className="text-xs text-gray-500 mt-1">
                        {new Date(log.date).toLocaleString('fr-FR')}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Info Box */}
      <div className="bg-teal-50/30 backdrop-blur-sm border border-teal-200/50 rounded-2xl p-5 hover:bg-teal-50/50 transition-all duration-300">
        <div className="flex items-start gap-4">
          <div className="p-2 bg-teal-100 rounded-xl">
            <Info className="w-5 h-5 text-teal-600" />
          </div>
          <div>
            <h4 className="font-semibold text-teal-900">Export Automatique</h4>
            <div className="text-sm text-teal-800 mt-2 space-y-1">
              <p><span className="font-medium">Données exportées :</span> Factures, Dépenses, Paiements</p>
              <p><span className="font-medium">Format :</span> Écritures comptables avec comptes PCG (classe 4, 6, 7)</p>
              <p><span className="font-medium">Fréquence :</span> Temps réel (si auto-export activé) ou manuel</p>
              <p><span className="font-medium">Connecteurs supportés :</span> Sage One, Cegid Expert, Quadra Compta</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AccountingConnectorsSettings;
